import { useCallback } from 'react';
import styled from '@emotion/styled';
import { tokens } from '@hotjar/hotsauce';
import { noop } from '@hotjar/webapp/lib/function';
import { event as HJEvent } from '@hotjar/tracking/src/tools/hotjar';
import { track } from '@hotjar/webapp/domains/common/tracking/segment';
import { useFeatureFlag } from '@hotjar/webapp/hooks/useFeatureFlag';

import { CONTROLS_AND_ELEMENTS_IDS } from '@hotjar/recordings/src/constants/heatmaps-retaker';

import { useStoreState } from '@hotjar/recordings/src/store/player/context';
import { ConsoleButton } from '@hotjar/webapp/features/Playbacks/Controls/ConsoleButton';
import { ReactionNameEnum, getReactionIdByName } from '../ReactionIcons';

import { CONTROL_HEIGHT } from './constants';
import { SpeedButton } from './SpeedButton';
import { ToggleInactivitySwitch } from './ToggleInactivitySwitch';
import { TimeInfo } from './TimeInfo';
import { RewindButton } from './RewindButton';
import { PlayPauseReplayButton } from './PlayPauseReplayButton';
import { FastForwardButton } from './FastForwardButton';
import { CreateHighlightButton } from './CreateHighlightButton';
import { ForcePlaybackPopup } from '../ForcePlaybackPopup';
import { SettingsButton } from './SettingsButton';
import { SettingsModal } from './Modals/SettingsModal';
import type { Recording } from '../types';
import { ModalStateEnum } from '../types';
import { AddReactionButton } from './AddReactionButton';
import { SkipCurrentInactivityButton } from './SkipCurrentInactivityButton';
import { getGroupDataForRecording, trackClick as clickTracker } from '../utils/tracking';
import { AddCustomLabelButton } from './AddCustomLabelButton';

const HIGHLIGHTL_LABELS_BREAKPOINT = 930;

const ControlsContainer = styled.div`
  ${tokens.text('subtitle3')};
  position: relative;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: ${CONTROL_HEIGHT}px;
  height: ${CONTROL_HEIGHT}px;
  padding: 0 ${tokens.spacer.x2};
  color: ${tokens.color.white};
  text-align: initial;
  font-weight: 600;

  margin-bottom: ${tokens.spacer.x2};

  @media (max-width: ${HIGHLIGHTL_LABELS_BREAKPOINT}px) {
    grid-template-columns: 1fr auto auto;
  }
`;

const LeftColumn = styled.div`
  width: 500px;

  margin-right: auto;
  display: flex;
  align-items: center;

  gap: ${tokens.spacer.x2};
`;

const MiddleColumn = styled.div`
  display: grid;
  grid-auto-flow: column;
  justify-content: center;
  align-items: center;
  margin-left: auto;
  margin-right: auto;
  vertical-align: middle;
`;

const StickersContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${tokens.spacer.x1};

  @media (max-width: ${HIGHLIGHTL_LABELS_BREAKPOINT}px) {
    display: none;
  }
`;

const RightColumn = styled.div`
  display: flex;
  align-items: center;
  margin-left: auto;
  justify-content: flex-end;

  gap: ${tokens.spacer.x2};
`;

const ControlsSpacer = styled.div`
  width: 1px;
  height: 32px;
  background: ${tokens.color.neutral_300};
`;

const trackShortcutsModalOpen = (recording: Recording) => {
  const trackingData = {
    ...getGroupDataForRecording(recording),
    modal_name: 'playback_shortcuts_legend',
    modal_version: 'shortcuts_legend',
    state_event_was_triggered_on: 'playback.player',
    ui_position: 'playback_clicked-view-keyboard-shortcuts',
  };

  track('Modal Loaded', trackingData);
};

const trackSettingsModalOpen = (recording: Recording) => {
  const trackingData = {
    ...getGroupDataForRecording(recording),
    modal_name: 'playback_settings',
    modal_version: 'playback_settings',
    state_event_was_triggered_on: 'playback.player_settings',
    ui_position: 'playback_settings_modal',
  };

  track('Modal Loaded', trackingData);
};

export interface ControlsProps {
  isTokenShared: boolean;
  currentSpeed: number;
  duration: number;
  onSpeedChange: () => void;
  isSkippingAllPauses: boolean;
  onSkipPausesToggle: (checked: boolean) => void;
  isJumpStartActive: boolean;
  onToggleJumpStart: () => void;
  isMuteClicksActive: boolean;
  onToggleMuteClicks: () => void;
  onRewind: (milliseconds: number) => void;
  onPlay: (showAnimation?: boolean) => void;
  onReplay: () => void;
  onPause: (showAnimation?: boolean, isManual?: boolean) => void;
  onSkipCurrentInactivity: () => void;
  onNewHighlight: () => void;
  onAddLabel: (labelId: string) => void;
  isPlaying: boolean;
  isDone: boolean;
  onForward: (milliseconds: number) => void;
  onAutoplayToggle: (checked: boolean) => void;
  isAutoplaying: boolean;
  loaded: boolean;
  recording: Recording;
  onForcePlaybackClose: () => void;
  isForcingPlayback: boolean;
  showStickerLabels: boolean;
  activeModal: ModalStateEnum;
  setActiveModal: (state: ModalStateEnum) => void;
  shouldTrack?: boolean;
  isConsoleOpen: boolean;
}

export const Controls = ({
  isTokenShared,
  currentSpeed,
  duration = 0,
  onSpeedChange,
  isSkippingAllPauses,
  onSkipPausesToggle,
  isJumpStartActive,
  onToggleJumpStart,
  isMuteClicksActive,
  onToggleMuteClicks,
  onRewind,
  onPlay,
  onReplay,
  onPause,
  onSkipCurrentInactivity,
  onNewHighlight,
  onAddLabel,
  isPlaying,
  isDone,
  onForward,
  onAutoplayToggle,
  isAutoplaying,
  loaded,
  recording,
  isForcingPlayback,
  onForcePlaybackClose,
  showStickerLabels,
  activeModal,
  setActiveModal,
  shouldTrack = true,
  isConsoleOpen,
}: ControlsProps): JSX.Element => {
  const isCustomLabelsAllowed = useFeatureFlag('settings.custom-labels');
  const trackClick = shouldTrack ? clickTracker : noop;
  const { toggleConsole } = useStoreState();

  const rewind = useCallback(() => {
    trackClick(recording, {
      element_name: 'playback_jumped_backward',
      state_event_was_triggered_on: 'playback.player_navigation',
      ui_position: 'clicked_icon-10s-rewind',
    });

    onRewind(10000);
  }, [onRewind, recording, trackClick]);
  const forward = useCallback(() => {
    trackClick(recording, {
      element_name: 'playback_jumped_forward',
      state_event_was_triggered_on: 'playback.player_navigation',
      ui_position: 'clicked_icon-10s-fast-forward',
    });

    onForward(10000);
  }, [onForward, recording, trackClick]);
  const play = useCallback(
    (showAnimation?: boolean) => {
      trackClick(recording, {
        element_name: 'playback_resumed',
        state_event_was_triggered_on: 'playback.player_navigation',
        ui_position: 'clicked_play',
      });

      onPlay(showAnimation);
    },
    [onPlay, recording, trackClick],
  );
  const pause = useCallback(
    (showAnimation?: boolean, isManual?: boolean) => {
      trackClick(recording, {
        element_name: 'playback_paused',
        state_event_was_triggered_on: 'playback.player_navigation',
        ui_position: 'clicked_pause',
      });

      onPause(showAnimation, isManual);
    },
    [onPause, recording, trackClick],
  );
  const replay = useCallback(() => {
    trackClick(recording, {
      element_name: 'playback_replayed',
      state_event_was_triggered_on: 'playback.player_navigation',
      ui_position: 'clicked_icon',
    });

    onReplay();
  }, [onReplay, recording, trackClick]);

  const onNewHighlightWrapper = useCallback(() => {
    trackClick(recording, {
      element_name: 'Create highlight - bulb click',
      state_event_was_triggered_on: 'playback.player',
      ui_position: 'clicked_on_bulb',
    });

    onNewHighlight();
  }, [onNewHighlight, recording, trackClick]);
  const isLoadingOrNoRecording = !loaded || !recording;
  const onSkipPausesToggleWrapper = useCallback(
    (isChecked) => {
      const elementNameSuffix = isChecked ? 'ON' : 'OFF';
      trackClick(recording, {
        element_name: `playback_toggled_skip_inactivity_${elementNameSuffix}`,
        state_event_was_triggered_on: 'playback.player_navigation',
        ui_position: 'clicked-toggle',
        url_event_was_triggered_on: window.location.href,
      });

      onSkipPausesToggle(!isSkippingAllPauses);
    },
    [onSkipPausesToggle, isSkippingAllPauses, recording, trackClick],
  );
  const onAutoplayToggleWrapper = useCallback(
    (isChecked) => {
      const elementNameSuffix = isChecked ? 'ON' : 'OFF';
      trackClick(recording, {
        element_name: `playback_toggled_autoplay_${elementNameSuffix}`,
        state_event_was_triggered_on: 'playback.player_settings',
        ui_position: 'clicked-toggle',
      });

      onAutoplayToggle(isChecked);
    },
    [onAutoplayToggle, recording, trackClick],
  );
  const onToggleJumpStartWrapper = useCallback(() => {
    const elementNameSuffix = !isJumpStartActive ? 'ON' : 'OFF';
    trackClick(recording, {
      element_name: `playback_toggled_jumpstart_${elementNameSuffix}`,
      state_event_was_triggered_on: 'playback.player_settings',
      ui_position: 'clicked-toggle',
    });

    onToggleJumpStart();
  }, [onToggleJumpStart, recording, isJumpStartActive, trackClick]);
  const onToggleMuteClicksWrapper = useCallback(() => {
    const elementNameSuffix = !isMuteClicksActive ? 'ON' : 'OFF';
    trackClick(recording, {
      element_name: `playback_toggled_mute-clicks_${elementNameSuffix}`,
      state_event_was_triggered_on: 'playback.player_settings',
      ui_position: 'clicked-toggle',
    });

    onToggleMuteClicks();
  }, [onToggleMuteClicks, recording, isMuteClicksActive, trackClick]);
  const openShortcutsModal = useCallback(() => {
    setActiveModal(ModalStateEnum.KEYBOARD_SHORTCUT_OPEN);
    trackShortcutsModalOpen(recording);
  }, [recording, setActiveModal]);

  const openSettingsModal = useCallback(() => {
    setActiveModal(ModalStateEnum.SETTINGS_OPEN);
    trackSettingsModalOpen(recording);
  }, [recording, setActiveModal]);

  const skipCurrentInactivity = useCallback(() => {
    trackClick(recording, {
      element_name: 'skip_single_period_of_inactivity',
      state_event_was_triggered_on: 'playback.player_navigation',
      ui_position: 'clicked_footer_button',
    });

    onSkipCurrentInactivity();
  }, [onSkipCurrentInactivity, recording, trackClick]);

  const onConsoleButtonClick = useCallback(() => {
    if (!isConsoleOpen) {
      HJEvent('Console Opened');
    }
    toggleConsole();
  }, [isConsoleOpen, toggleConsole]);

  return (
    <ControlsContainer>
      <LeftColumn id={CONTROLS_AND_ELEMENTS_IDS.PLAYER_CONTROLS_CONTAINER}>
        <span id={CONTROLS_AND_ELEMENTS_IDS.PLAY_PAUSE_BUTTON}>
          <PlayPauseReplayButton
            isPlaying={isPlaying}
            isDone={isDone}
            onPause={pause}
            onPlay={play}
            onReplay={replay}
            disabled={isLoadingOrNoRecording}
          />
        </span>
        <RewindButton onClick={rewind} disabled={isLoadingOrNoRecording} />
        <FastForwardButton disabled={isLoadingOrNoRecording || isDone} onClick={forward} />
        <ControlsSpacer />
        <TimeInfo
          id={CONTROLS_AND_ELEMENTS_IDS.PLAYER_TIME_INFO}
          duration={duration}
          disabled={isLoadingOrNoRecording}
        />
        <SpeedButton
          onClick={onSpeedChange}
          currentSpeed={currentSpeed}
          disabled={isLoadingOrNoRecording}
        />
        <ControlsSpacer />
        <ToggleInactivitySwitch
          onSkipPausesToggle={onSkipPausesToggleWrapper}
          isSkippingAllPauses={isSkippingAllPauses}
        />
        {!isLoadingOrNoRecording && !isSkippingAllPauses && (
          <SkipCurrentInactivityButton onClick={skipCurrentInactivity} />
        )}
      </LeftColumn>

      <MiddleColumn id={CONTROLS_AND_ELEMENTS_IDS.HIGHLIGHTS_REACTIONS_CONTAINER}>
        {!isTokenShared && (
          <>
            <CreateHighlightButton
              onClick={onNewHighlightWrapper}
              disabled={isLoadingOrNoRecording}
            />
            {showStickerLabels && (
              <StickersContainer>
                <ControlsSpacer />
                <AddReactionButton
                  reactionName={ReactionNameEnum.bug}
                  onClick={() => onAddLabel(getReactionIdByName(ReactionNameEnum.bug))}
                  disabled={isLoadingOrNoRecording}
                />
                <AddReactionButton
                  reactionName={ReactionNameEnum.uxUiIssue}
                  onClick={() => onAddLabel(getReactionIdByName(ReactionNameEnum.uxUiIssue))}
                  disabled={isLoadingOrNoRecording}
                />
                <AddReactionButton
                  reactionName={ReactionNameEnum.confusion}
                  onClick={() => onAddLabel(getReactionIdByName(ReactionNameEnum.confusion))}
                  disabled={isLoadingOrNoRecording}
                />
                <AddReactionButton
                  reactionName={ReactionNameEnum.frustration}
                  onClick={() => onAddLabel(getReactionIdByName(ReactionNameEnum.frustration))}
                  disabled={isLoadingOrNoRecording}
                />
                <AddReactionButton
                  reactionName={ReactionNameEnum.success}
                  onClick={() => onAddLabel(getReactionIdByName(ReactionNameEnum.success))}
                  disabled={isLoadingOrNoRecording}
                />
                <AddReactionButton
                  reactionName={ReactionNameEnum.conversion}
                  onClick={() => onAddLabel(getReactionIdByName(ReactionNameEnum.conversion))}
                  disabled={isLoadingOrNoRecording}
                />
                {isCustomLabelsAllowed && (
                  <AddCustomLabelButton siteId={String(recording?.site_id) ?? null} />
                )}
              </StickersContainer>
            )}
          </>
        )}
      </MiddleColumn>

      <RightColumn id={CONTROLS_AND_ELEMENTS_IDS.RECORDING_CONTROLS_CONTAINER}>
        <ConsoleButton
          onClick={onConsoleButtonClick}
          isOpen={isConsoleOpen}
          recording={recording}
          disabled={isLoadingOrNoRecording}
        />

        <SettingsButton
          onClick={openSettingsModal}
          disabled={isLoadingOrNoRecording}
          isSettingsModalOpen={activeModal === ModalStateEnum.SETTINGS_OPEN}
        />
      </RightColumn>

      <ForcePlaybackPopup
        accountId={recording?.account_id}
        siteId={recording?.site_id}
        organizationId={recording?.organization_id}
        isShown={isForcingPlayback}
        onClose={onForcePlaybackClose}
      />

      <SettingsModal
        isOpen={activeModal === ModalStateEnum.SETTINGS_OPEN}
        onClose={() => setActiveModal(ModalStateEnum.CLOSE)}
        onOpenKeyboardShorcutModal={openShortcutsModal}
        onAutoplayToggle={onAutoplayToggleWrapper}
        isAutoplaying={isAutoplaying}
        isJumpStartActive={isJumpStartActive}
        onToggleJumpStart={onToggleJumpStartWrapper}
        isMuteClicksActive={isMuteClicksActive}
        onToggleMuteClicks={onToggleMuteClicksWrapper}
        isPlaying={isPlaying}
        onPause={onPause}
        onPlay={onPlay}
        activeModal={activeModal}
      />
    </ControlsContainer>
  );
};

// FIXME: These defaults are reduntant when declared for required props.
//        Either remove them or change props to optional.
/* eslint-disable react/default-props-match-prop-types */
Controls.defaultProps = {
  isTokenShared: false,
  currentSpeed: 0,
  duration: 0,
  onSpeedChange: undefined,
  isSkippingAllPauses: false,
  onSkipPausesToggle: undefined,
  isJumpStartActive: false,
  isMuteClicksActive: false,
  onRewind: undefined,
  onPlay: undefined,
  onReplay: undefined,
  onPause: undefined,
  onNewHighlight: undefined,
  onNewComment: undefined,
  onAddLabel: undefined,
  isPlaying: false,
  isDone: false,
  onForward: undefined,
  hasPreviousRecording: false,
  onPrevious: undefined,
  hasNextRecording: false,
  onNext: undefined,
  onAutoplayToggle: undefined,
  isAutoplaying: false,
  loaded: false,
  recording: undefined,
  onForcePlaybackClose: undefined,
  isForcingPlayback: false,
  activeModal: ModalStateEnum.CLOSE,
  setActiveModal: undefined,
};
